import * as React from "react";
import { X, ChevronsUpDown, ChevronRight, ListTree } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTran } from "@/hooks";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { Globe } from "lucide-react";
import { useMemo } from "react";

// ─────────── FILTER TREE: KEEP PARENT + MATCH + CHILD ───────────
{
  /** 
  const filterTreeKeepGroup = (nodes = [], query = "") => {
      if (!query) return nodes;
      const q = query.toLowerCase();
  
      const visit = (node) => {
          const nodeText = [node.name, node.code].filter(Boolean).join(" ").toLowerCase();
          const children = Array.isArray(node.children) ? node.children : [];
  
          const matchedChildren = children.map(visit).filter(Boolean);
          const isMatch = nodeText.includes(q);
  
          if (isMatch) return { ...node, children };
          if (matchedChildren.length) return { ...node, children: matchedChildren };
  
          return null;
      };
  
      return nodes.map(visit).filter(Boolean);
  };
  
  */
}

// ─────────── FILTER TREE: KEEP PARENT + MATCH (not contain Childs) ───────────
const filterTreeKeepGroup = (nodes = [], query = "") => {
  if (!query) return nodes;

  const q = query.toLowerCase();

  const visit = (node) => {
    const nodeText = [node.name, node.code]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const children = Array.isArray(node.children) ? node.children : [];

    const isMatch = nodeText.includes(q);

    // if node matches -> return it without children
    if (isMatch) {
      return { ...node, children: [] };
    }

    // search children
    const matchedChildren = children.map(visit).filter(Boolean);

    // if child matched -> keep parent chain
    if (matchedChildren.length) {
      return { ...node, children: matchedChildren };
    }

    return null;
  };

  return nodes.map(visit).filter(Boolean);
};

// ─────────── FLATTEN TREE ───────────
// ─────────── FLATTEN TREE WITH FULL PARENT CHAIN ───────────
const flattenTreeWithDepth = (tree = [], depth = 0, parentChain = []) => {
  const result = [];

  const visit = (nodes, d, parents) => {
    (nodes || []).forEach((node) => {
      //  handle nested arrays
      if (Array.isArray(node)) {
        visit(node, d, parents);
        return;
      }

      const value = node.id || node._id || node.key;
      if (!value) return;

      // full parent chain for this node
      const fullParentChain = [...parents];

      result.push({
        value,
        label: node.name || value,
        depth: d,
        rawNode: node,
        parentChain: fullParentChain, // use this in isVisible
      });

      if (Array.isArray(node.children) && node.children.length) {
        visit(node.children, d + 1, [
          ...fullParentChain,
          { value, label: node.name || value, status: node.status },
        ]);
      }
    });
  };

  visit(tree, depth, parentChain);
  return result;
};

const FOR_ALL_ORGANIZATIONS_VALUE = "for_all_organizations";

// ─────────── COMPONENT ───────────
export const OrgTreeNodeMultiFilter = ({
  value = [],
  onChange,
  tree = [],
  placeholder,
  disabled = false,
  className,
  hasItemWithAllOrgs = false,
  filteredOrganizationIds = [],
}) => {
  const { t } = useTran();
  const [open, setOpen] = React.useState(false);
  const [viewMode] = React.useState("tree"); // fixed to tree
  const [searchQuery, setSearchQuery] = React.useState("");

  // ─────────── FILTER TREE BASED ON SEARCH ───────────
  const filteredTree = React.useMemo(() => {
    // remove outermost parent
    // const baseTree = tree.flatMap((node) => node.children || []);

    const baseTree = tree;

    if (!searchQuery) return baseTree;

    return filterTreeKeepGroup(baseTree, searchQuery);
  }, [tree, searchQuery]);

  // ─────────── FLATTEN TREE FOR RENDERING ───────────
  const options = React.useMemo(
    () => flattenTreeWithDepth(filteredTree),
    [filteredTree],
  );

  const allowedOrgSet = useMemo(() => {
    return new Set(filteredOrganizationIds);
  }, [filteredOrganizationIds]);

  // ─────────── HANDLE SELECTION ───────────
  const handleSelect = (val) => {
    const newValue = value.includes(val)
      ? value.filter((v) => v !== val)
      : [...value, val];
    onChange(newValue);
  };

  const truncateLabel = (text, maxLength = 15) =>
    text && text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

  const selectedOptions = React.useMemo(() => {
    const selected = options.filter((option) => value.includes(option.value));

    if (value.includes(FOR_ALL_ORGANIZATIONS_VALUE)) {
      selected.unshift({
        value: FOR_ALL_ORGANIZATIONS_VALUE,
        label: t("For All Organization"),
      });
    }

    return selected;
  }, [options, t, value]);

  // ─────────── INITIAL EXPAND ───────────
  const getAllParentNodes = (nodes = []) => {
    const expanded = {};
    const visit = (nodes) => {
      (nodes || []).forEach((node) => {
        const value = node.id || node._id || node.key;
        if (node.children?.length) {
          expanded[value] = true;
          visit(node.children);
        }
      });
    };
    visit(nodes);
    return expanded;
  };

  const [expandedNodes, setExpandedNodes] = React.useState(() =>
    getAllParentNodes(tree),
  );

  // ─────────── EXPAND ALL WHEN SEARCH ───────────
  React.useEffect(() => {
    if (!searchQuery) return;

    const newExpanded = {};
    const expandParents = (nodes) => {
      (nodes || []).forEach((node) => {
        const value = node.id || node._id || node.key;
        if (node.children?.length) {
          newExpanded[value] = true;
          expandParents(node.children);
        }
      });
    };

    expandParents(filteredTree);
    setExpandedNodes(newExpanded);
  }, [searchQuery, filteredTree]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between h-[35px]", className)}
          disabled={disabled}
        >
          <div className="flex flex-1 flex-wrap gap-1">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">
                {placeholder || t("Select organizations")}
              </span>
            ) : (
              <>
                {selectedOptions.slice(0, 2).map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="mr-1 gap-1 flex items-center"
                  >
                    <div className="flex items-center gap-1">
                      {truncateLabel(option.label)}
                      <OrgStatusTag
                        status={option.rawNode?.status || option.status}
                        inactiveDate={option.rawNode?.inactiveDate}
                        className="text-[10px]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(option.value);
                      }}
                      className="ml-1 rounded-full"
                    >
                      <X className="h-3 w-3 text-gray-200 hover:text-foreground" />
                    </button>
                  </Badge>
                ))}
                {selectedOptions.length > 2 && (
                  <Badge variant="secondary">
                    +{selectedOptions.length - 2}
                  </Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-full p-0" align="start">
        {/* Search input */}
        <div className="border-b p-2">
          <div className="flex items-center border rounded-md px-2">
            <input
              type="text"
              placeholder={t("Search organization...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 text-sm outline-none bg-transparent"
            />
          </div>
        </div>

        {/* Tree */}
        <div className="max-h-[250px] overflow-y-auto">
          {options.length === 0 && !hasItemWithAllOrgs && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t("No organizations available")}
            </div>
          )}

          {hasItemWithAllOrgs && (
            <div>
              <div
                className="cursor-pointer hover:bg-muted rounded-sm transition-colors"
                style={{ paddingLeft: "10px" }} // no indent (top level)
                //onClick={() => handleSelect(FOR_ALL_ORGANIZATIONS_VALUE)}
              >
                <div className="flex min-h-[44px] items-center justify-between px-3 py-1.5">
                  <div className="flex min-w-0 flex-1 items-center gap-2 pr-3">
                    {/* No expand arrow (keep spacing aligned) */}
                    <span className="h-6 w-6 shrink-0" />

                    {/* Icon */}
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                      <Globe className="h-4 w-4 text-primary" />
                    </div>

                    {/* Label */}
                    <span className="min-w-0 truncate text-sm">
                      {t("For All Organizations")}
                    </span>
                  </div>

                  {/* Checkbox */}
                  {/* {allowedOrgSet.has("for_all_organizations") && ( */}
                  <Checkbox
                    className="shrink-0"
                    checked={value.includes(FOR_ALL_ORGANIZATIONS_VALUE)}
                    onCheckedChange={() =>
                      handleSelect(FOR_ALL_ORGANIZATIONS_VALUE)
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                  {/* )} */}
                </div>
              </div>
            </div>
          )}

          {options.map((option) => {
            const depth = option.depth ?? 0;
            const isSelected = value.includes(option.value);
            const meta = getUnitTypeMeta(option?.rawNode?.unitLevel);
            const Icon = meta.icon;
            const isVisible = (node) => {
              return node.parentChain.every(
                (parent) => expandedNodes[parent.value],
              );
            };

            if (!isVisible(option)) return null;

            return (
              <div key={option.value}>
                <div
                  className="cursor-pointer hover:bg-muted rounded-sm transition-colors"
                  style={{
                    paddingLeft:
                      viewMode === "tree" ? `${depth * 20 + 10}px` : "10px",
                  }}
                  onClick={() => {
                    if (allowedOrgSet.has(String(option.rawNode.id))) {
                      handleSelect(option.value);
                    }
                  }}
                >
                  <div className="flex min-h-[44px] items-center justify-between px-3 py-1.5">
                    <div className="flex min-w-0 flex-1 items-center gap-2 pr-3">
                      {/* Expand arrow */}
                      {viewMode === "tree" &&
                      option.rawNode?.children?.length ? (
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
                          <ChevronRight
                            className={cn(
                              "h-4 w-4 text-muted-foreground transition-transform duration-200",
                              expandedNodes[option.value] ? "rotate-90" : "",
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedNodes({
                                ...expandedNodes,
                                [option.value]: !expandedNodes[option.value],
                              });
                            }}
                          />
                        </div>
                      ) : (
                        <span className="h-6 w-6 shrink-0" />
                      )}

                      {/* Icon */}
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                        <Icon className={`h-4 w-4 ${meta.color}`} />
                      </div>

                      {/* Label */}
                      <div className="flex flex-col gap-0.5">
                        <span className="min-w-0 truncate text-sm">
                          {option.label}
                        </span>
                        <OrgStatusTag
                          status={option.rawNode?.status || option.status}
                          inactiveDate={option.rawNode?.inactiveDate}
                          className="text-[10px]"
                        />
                      </div>
                    </div>
                    {/* Checkbox */}
                    {allowedOrgSet.has(String(option.rawNode.id)) && (
                      <Checkbox
                        className="shrink-0"
                        checked={isSelected}
                        onCheckedChange={() => handleSelect(option.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
